import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from '@tanstack/react-router';
import { Search, Bell, Globe, ChevronDown, MessageSquare } from 'lucide-react';
import { useCurrentUser } from '@/hooks/useAuth';
import {
  ADMIN_MODULES,
  findActiveModule,
  filterModulesByPermission,
  isActiveModule,
} from '@/navigation/modules';
import { UserMenu } from '@/layouts/UserMenu';

function usePermissionGate() {
  const { data: user } = useCurrentUser();
  const permissions = new Set(user?.permissions ?? []);
  const isSuper = Boolean(user?.is_super_admin || user?.role_id === 1);
  const can = (key?: string) => {
    if (!key) return true;
    if (isSuper) return true;
    return permissions.has(key) || permissions.has('all') || permissions.has('*');
  };
  return { can, user };
}

export function useAdminModules() {
  const { can } = usePermissionGate();
  return filterModulesByPermission(ADMIN_MODULES, can);
}

export function useActiveModule() {
  const location = useLocation();
  return findActiveModule(location.pathname);
}

/** Desktop global navbar (>= 1024px). */
export function DesktopNavbar({ onToggleSidebar, onOpenSearch }: { onToggleSidebar?: () => void; onOpenSearch?: () => void }) {
  const location = useLocation();
  const navigate = useNavigate();
  const modules = useAdminModules();
  const { can } = usePermissionGate();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const settingsRef = useRef<HTMLLIElement>(null);
  const pathname = location.pathname;

  useEffect(() => {
    if (!settingsOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (!settingsRef.current?.contains(e.target as Node)) setSettingsOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSettingsOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [settingsOpen]);

  return (
    <nav className="top-navbar desktop-navbar" aria-label="Global module navigation">
      {onToggleSidebar ? (
        <button
          type="button"
          className="top-nav-icon-btn desktop-only-icon"
          aria-label="Toggle sidebar"
          onClick={onToggleSidebar}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
            <path d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
      ) : null}

      <ul className="top-navbar-tabs">
        {modules.map((mod) => {
          const isActive = isActiveModule(mod, pathname);
          const Icon = mod.icon;

          if (mod.children?.length) {
            return (
              <li
                key={mod.id}
                ref={settingsRef}
                className={`top-nav-item ${isActive || settingsOpen ? 'active' : ''}`}
              >
                <button
                  type="button"
                  className="top-nav-link"
                  aria-expanded={settingsOpen}
                  aria-haspopup="menu"
                  onClick={() => setSettingsOpen((v) => !v)}
                >
                  <Icon size={17} aria-hidden />
                  <span>{mod.label}</span>
                  <ChevronDown size={14} aria-hidden className={settingsOpen ? 'chev-up' : ''} />
                </button>
                {settingsOpen ? (
                  <div className="top-nav-dropdown" role="menu">
                    <div className="top-nav-dropdown-header">
                      <strong>{mod.label}</strong>
                      <p>Monitor your business general settings from here</p>
                    </div>
                    <div className="top-nav-dropdown-body">
                      {mod.children.map((child) =>
                        can(child.permission) ? (
                          <Link
                            key={child.to}
                            to={child.to as never}
                            role="menuitem"
                            onClick={() => setSettingsOpen(false)}
                          >
                            {child.label}
                          </Link>
                        ) : null,
                      )}
                      <button
                        type="button"
                        className="top-nav-dropdown-all"
                        onClick={() => {
                          setSettingsOpen(false);
                          void navigate({ to: mod.route as never });
                        }}
                      >
                        View All
                      </button>
                    </div>
                  </div>
                ) : null}
              </li>
            );
          }

          return (
            <li key={mod.id} className={`top-nav-item ${isActive ? 'active' : ''}`}>
              <Link to={mod.route as never} className="top-nav-link" aria-current={isActive ? 'page' : undefined}>
                <Icon size={17} aria-hidden />
                <span>{mod.label}</span>
              </Link>
            </li>
          );
        })}
      </ul>

      <div className="top-navbar-right">
        <button type="button" className="top-nav-search" aria-label="Search" title="Search (Ctrl+K)" onClick={onOpenSearch}>
          <Search size={16} aria-hidden />
          <span className="top-nav-search-label">Search anything...</span>
          <kbd>Ctrl + K</kbd>
        </button>
        <button type="button" className="top-nav-icon-btn has-badge" aria-label="Notifications" title="Notifications">
          <Bell size={18} aria-hidden />
          <span className="notif-dot" aria-hidden />
        </button>
        <button type="button" className="top-nav-lang" aria-label="Language" title="Language">
          <Globe size={16} aria-hidden />
          <span>En</span>
          <ChevronDown size={13} aria-hidden />
        </button>
        <Link to={'/messages' as never} className="top-nav-icon-btn" aria-label="Messages" title="Messages">
          <MessageSquare size={18} aria-hidden />
        </Link>
        <UserMenu variant="navbar" />
      </div>
    </nav>
  );
}

/** Mobile header (< 1024px) with module selector dropdown. */
export function MobileHeader({
  onOpenDrawer,
  onOpenSearch,
  moduleMenuOpen,
  onToggleModuleMenu,
  onCloseModuleMenu,
}: {
  onOpenDrawer: () => void;
  onOpenSearch?: () => void;
  moduleMenuOpen: boolean;
  onToggleModuleMenu: () => void;
  onCloseModuleMenu: () => void;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const modules = useAdminModules();
  const active = useActiveModule();
  const rootRef = useRef<HTMLDivElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!moduleMenuOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) onCloseModuleMenu();
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [moduleMenuOpen, onCloseModuleMenu]);

  const selectModule = (route: string) => {
    onCloseModuleMenu();
    void navigate({ to: route as never });
  };

  return (
    <header className="mobile-header" ref={rootRef}>
      <button
        ref={menuButtonRef}
        type="button"
        className="mobile-menu-button"
        aria-label="Open navigation"
        onClick={() => {
          onCloseModuleMenu();
          onOpenDrawer();
        }}
      >
        <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
          <path d="M4 6h16M4 12h16M4 18h16" />
        </svg>
      </button>

      <Link to={'/dashboard' as never} className="mobile-brand" aria-label="Ezzo Admin home">
        <span className="brand-mark">E</span>
        <span className="brand-text">Ezzo Admin</span>
      </Link>

      <div className="mobile-module-wrap">
        <button
          type="button"
          className={`mobile-module-trigger ${moduleMenuOpen ? 'open' : ''}`}
          onClick={onToggleModuleMenu}
          aria-expanded={moduleMenuOpen}
          aria-haspopup="listbox"
          aria-label="Select module"
        >
          <span>{active?.label ?? 'Menu'}</span>
          <ChevronDown size={14} aria-hidden className={moduleMenuOpen ? 'chev-up' : ''} />
        </button>

        {moduleMenuOpen ? (
          <div className="mobile-module-dropdown" role="listbox" aria-label="Application modules">
            {modules.map((mod) => {
              const Icon = mod.icon;
              const isActive = isActiveModule(mod, location.pathname);
              return (
                <button
                  key={mod.id}
                  type="button"
                  role="option"
                  aria-selected={isActive}
                  className={`mobile-module-item ${isActive ? 'active' : ''}`}
                  onClick={() => selectModule(mod.route)}
                >
                  <Icon size={18} aria-hidden />
                  <span>{mod.label}</span>
                  {isActive ? <span className="module-check" aria-hidden>✓</span> : null}
                </button>
              );
            })}
          </div>
        ) : null}
      </div>

      <div className="mobile-header-actions">
        <button type="button" className="top-nav-icon-btn" aria-label="Search" onClick={onOpenSearch}>
          <Search size={18} aria-hidden />
        </button>
        <button type="button" className="top-nav-icon-btn has-badge" aria-label="Notifications">
          <Bell size={18} aria-hidden />
          <span className="notif-dot" aria-hidden />
        </button>
        <UserMenu variant="avatar" />
      </div>
    </header>
  );
}
